import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAgentInfo } from "../_context/AgentInfoContext";
import profileAvatar from "assets/img/avatars/avatarSimmmple.png";
import { HiAcademicCap, HiCog, HiDocumentReport, HiInformationCircle, HiShieldCheck } from "react-icons/hi";
import Sidebar from "components/Sidebar/Sidebar";
import { getAgentStatusColor, getAgentStatusTranslationKey } from "utils";
import { Image } from "components/Image";
import { GitFork, Lightbulb, Scale, Users } from "lucide-react";
import { FaLink } from "react-icons/fa";
import {
  AGENT_RUN_STATUS,
  AGENT_TYPES,
  AUTONOMOUS_AGENT_STATUS,
  CREATE_AGENT_CATEGORIES_OPTIONS,
  FUNCTIONAL_AGENT_TYPE_OPTIONS
} from "constants/agents";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { startAgentRun, stopAgentRun, triggerAgentRun } from "api/axios";

const AgentSidebar = () => {
  const { id } = useParams();
  const { agentInfo, setAgentDetails, reloadLogs } = useAgentInfo();
  const isOrchestrator = agentInfo.agent_type === AGENT_TYPES.ORCHESTRATOR;
  const isAutonomous = agentInfo.agent_type === AGENT_TYPES.AUTONOMOUS;
  const { t } = useTranslation();
  const [isTriggering, setIsTriggering] = useState(false);
  const [isUpdatingSchedule, setIsUpdatingSchedule] = useState(false);
  const [currentScheduleStatus, setCurrentScheduleStatus] = useState(
    agentInfo.schedule_status
  );

  useEffect(() => {
    setCurrentScheduleStatus(agentInfo.schedule_status);
  }, [agentInfo.schedule_status]);

  // Get the display label for agent type
  const getAgentTypeLabel = () => {
    // Check if it's a functional agent type (like AUTONOMOUS)
    const functionalType = FUNCTIONAL_AGENT_TYPE_OPTIONS.find(
      (option) => option.value === (agentInfo.agent_type as any)
    );
    if (functionalType) {
      return t(functionalType.labelKey);
    }

    // Check if it's a standard agent type
    const agentType = CREATE_AGENT_CATEGORIES_OPTIONS.find(
      (option) => option.value === agentInfo.agent_type
    );
    if (agentType) {
      return t(agentType.labelKey);
    }

    // Fallback to raw value
    return agentInfo.agent_type;
  };

  const functionalItems = [
    {
      path: `/dashboard/agents/info/${id}`,
      label: t("agents.sidebar.agentInfo"),
      icon: <HiInformationCircle className="h-5 w-5" />,
    },
    {
      path: `/dashboard/agents/info/${id}/trigger`,
      label: t("agents.trigger.label"),
      icon: <HiCog className="h-5 w-5" />,
    },
    {
      path: `/dashboard/agents/info/${id}/pipeline`,
      label: t("agents.pipeline.label"),
      icon: <GitFork className="h-5 w-5" />,
    },
    {
      path: `/dashboard/agents/info/${id}/logs`,
      label: t("agents.logs.label"),
      icon: <HiDocumentReport className="h-5 w-5" />,
    },
    {
      path: `/dashboard/agents/info/${id}/settings`,
      label: t("agents.settings.label"),
      icon: <HiCog className="h-5 w-5" />,
    },
  ];

  const baseItems = [
    {
      path: `/dashboard/agents/info/${id}`,
      label: t("agents.sidebar.agentInfo"),
      icon: <HiInformationCircle className="h-5 w-5" />,
    },
    // Knowledge tab is only for specialists, not orchestrators
    ...(isOrchestrator
      ? []
      : [
          {
            path: `/dashboard/agents/info/${id}/knowledge-base`,
            label: t("agents.sidebar.knowledge"),
            icon: <HiAcademicCap className="h-5 w-5" />,
          },
        ]),
    {
      path: `/dashboard/agents/info/${id}/workflows`,
      label: t("agents.workflows.label"),
      icon: <GitFork className="h-5 w-5" />,
    },
    {
      path: `/dashboard/agents/info/${id}/evaluate/datasets`,
      label: t("agents.evaluate"),
      icon: <Scale className="h-5 w-5" />,
    },
    {
      path: `/dashboard/agents/info/${id}/acl/roles`,
      label: t("agents.aclLabel"),
      icon: <HiShieldCheck className="h-5 w-5" />,
    },
    {
      path: `/dashboard/agents/info/${id}/settings`,
      label: t("agents.settings.label"),
      icon: <HiCog className="h-5 w-5" />,
    },
  ];

  const items = [
    ...baseItems,
    // Conditionally add Integrations or Assigned Specialists
    ...(isOrchestrator
      ? [
          {
            path: `/dashboard/agents/info/${id}/assigned-specialists`,
            label: "Assigned Specialists",
            icon: <Users className="h-5 w-5" />,
          },
        ]
      : [
          {
            path: `/dashboard/agents/info/${id}/integrations`,
            label: t("agents.integrations"),
            icon: <FaLink className="h-5 w-5" />,
          },
        ]),
    {
      path: `/dashboard/agents/info/${id}/suggestions`,
      label: t("agents.suggestions"),
      icon: <Lightbulb className="h-5 w-5" />,
    },
  ];

  const isActiveItems = (itemPath: string) => {
    const currentPath = window.location.pathname;
    if (currentPath === itemPath) return true;
    if (itemPath.includes("/acl")) {
      return currentPath.includes("/acl");
    }
    if (itemPath.includes("/evaluate")) {
      return currentPath.includes("/evaluate");
    }
    if (itemPath === `/dashboard/agents/info/${id}`) {
      return currentPath === `/dashboard/agents/info/${id}/`;
    }
    return false;
  };

  const handleScheduleChange = () => {
    return async () => {
      if (isUpdatingSchedule) return;
      const isRunning = [
        AGENT_RUN_STATUS.RUNNING,
        AGENT_RUN_STATUS.PENDING,
      ].includes(currentScheduleStatus);
      setIsUpdatingSchedule(true);
      try {
        isRunning
          ? await stopAgentRun(agentInfo.id)
          : await startAgentRun(agentInfo.id);

        const nextStatus = isRunning
          ? AGENT_RUN_STATUS.CANCELLED
          : AGENT_RUN_STATUS.RUNNING;

        setCurrentScheduleStatus(nextStatus);
        if (agentInfo) {
          setAgentDetails({
            ...agentInfo,
            schedule_status: nextStatus,
          });
        }
      } catch (error) {
        console.error("Failed to update agent schedule status:", error);
        toast.error(t("agents.schedule.errors.updateFailed"));
      } finally {
        setIsUpdatingSchedule(false);
      }
    };
  };
  const triggerAgentAction = () => {
    return async () => {
      if (isTriggering) return;
      setIsTriggering(true);
      try {
        await triggerAgentRun(agentInfo.id);
        reloadLogs();
      } catch (error) {
        console.error("Failed to trigger agent:", error);
        toast.error(t("agents.trigger.errors.updateFailed"));
      } finally {
        setIsTriggering(false);
      }
    };
  };

  const ProfileSection = (
    <div className="flex flex-col items-center justify-center px-6 py-5">
      <div className="mb-3 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-gray-200 dark:bg-navy-700">
        <Image
          src={agentInfo.icon_uri}
          fallbackSrc={profileAvatar}
          alt={agentInfo.name}
          className="h-16 w-16 object-cover"
        />
      </div>
      <div className="flex items-center text-center sm:flex-row sm:justify-center sm:gap-3">
        <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
          {agentInfo.name}
        </h3>
        <span
          className={`mt-1 rounded-full px-2 py-0.5 text-[10px] font-medium text-white sm:mt-0 ${getAgentStatusColor(
            agentInfo.status
          )}`}
        >
          {t(getAgentStatusTranslationKey(agentInfo.status))}
        </span>
      </div>
      {agentInfo.agent_type && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {t("agents.type")}:
          </span>
          <span
            className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${
              isOrchestrator
                ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                : isAutonomous
                ? "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300"
                : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
            }`}
          >
            {getAgentTypeLabel()}
          </span>
        </div>
      )}
      {isAutonomous && !agentInfo.is_archived && (
        <div className="mt-4 flex w-full flex-col justify-center gap-2 sm:flex-row">
          {agentInfo.schedule_type === AUTONOMOUS_AGENT_STATUS.SCHEDULING && (
            <button
              onClick={handleScheduleChange()}
              disabled={isUpdatingSchedule}
              className={`rounded-lg px-4 py-2 text-center text-sm font-medium text-white transition duration-200 ${
                isUpdatingSchedule
                  ? "cursor-not-allowed bg-gray-400 opacity-50 dark:bg-gray-600"
                  : [
                      AGENT_RUN_STATUS.RUNNING,
                      AGENT_RUN_STATUS.PENDING,
                    ].includes(currentScheduleStatus)
                  ? "bg-red-400 hover:bg-red-500 dark:bg-red-400 dark:hover:bg-red-500"
                  : "bg-green-400 hover:bg-green-500 dark:bg-green-400 dark:hover:bg-green-500"
              }`}
            >
              {[AGENT_RUN_STATUS.RUNNING, AGENT_RUN_STATUS.PENDING].includes(
                currentScheduleStatus
              )
                ? t("agents.schedule.stop")
                : t("agents.schedule.start")}
            </button>
          )}
          <button
            onClick={triggerAgentAction()}
            disabled={isTriggering}
            className={`rounded-lg px-4 py-2 text-center text-sm font-medium text-white transition duration-200 ${
              isTriggering
                ? "cursor-not-allowed bg-gray-400 opacity-50 dark:bg-gray-600"
                : "bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600"
            }`}
          >
            {t("agents.trigger.title")}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Sidebar
      items={isAutonomous ? functionalItems : items}
      isActiveItems={isActiveItems}
      ProfileSection={ProfileSection}
    />
  );
};

export default AgentSidebar;
